import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PrimaryFullScreenSpinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  useAppForm,
} from "@repo/ui";
import { neverFunc } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal } from "lucide-react";
import { Suspense, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  AdminNonProcessingDayIcsCalendarRowFragment,
  AdminNonProcessingDayRowFragment,
  NonProcessingDaySource,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/non-processing-days")({
  head: () => opsHead("Non-Processing Days"),
  component: View,
});

function View() {
  return (
    <AdminPageContainer testId="admin-non-processing-days">
      <AdminPageHeader
        title="Non-Processing Days"
        subtitle="Manage days on which payments are not processed (public holidays), and the ICS calendars synced daily into them."
      />
      <Tabs defaultValue="days">
        <TabsList size="sm" className="mb-4">
          <TabsTrigger value="days">Days</TabsTrigger>
          <TabsTrigger value="calendars">ICS Calendars</TabsTrigger>
        </TabsList>
        <TabsContent value="days">
          <Suspense fallback={<PrimaryFullScreenSpinner />}>
            <DaysTab />
          </Suspense>
        </TabsContent>
        <TabsContent value="calendars">
          <Suspense fallback={<PrimaryFullScreenSpinner />}>
            <CalendarsTab />
          </Suspense>
        </TabsContent>
      </Tabs>
    </AdminPageContainer>
  );
}

// ---------------------------------------------------------------------------
// Days tab
// ---------------------------------------------------------------------------

const adminNonProcessingDaysDocument = graphql(`
  query AdminNonProcessingDays {
    nonProcessingDays {
      ...AdminNonProcessingDayRow
    }
  }

  fragment AdminNonProcessingDayRow on NonProcessingDay {
    id
    date
    name
    tz
    source
    createdAt
    updatedAt
  }
`);

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const DEFAULT_TZ = "Australia/Sydney";

const dayFormSchema = z.object({
  date: z
    .string({ message: "Please choose a date" })
    .regex(ISO_DATE_REGEX, { message: "Please choose a valid date" }),
  name: z.string().trim().min(1, { message: "Please enter a name" }),
  tz: z.string().trim().min(1, { message: "Please enter a timezone" }),
});

type DayFormSchema = z.input<typeof dayFormSchema>;

const importSchema = z.object({
  url: z.string().trim().url({ message: "Please enter a valid URL" }),
});

type ImportSchema = z.input<typeof importSchema>;

function DaysTab() {
  const { data } = useSuspenseQuery(adminNonProcessingDaysDocument);
  const [editingRow, setEditingRow] =
    useState<AdminNonProcessingDayRowFragment | null>(null);
  const [deletingRow, setDeletingRow] =
    useState<AdminNonProcessingDayRowFragment | null>(null);
  const [upsertDialogOpen, setUpsertDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const [createNonProcessingDay] = useMutation(
    graphql(`
      mutation CreateNonProcessingDay($input: CreateNonProcessingDayInput!) {
        createNonProcessingDay(input: $input) {
          nonProcessingDay {
            ...AdminNonProcessingDayRow
          }
        }
      }
    `),
  );

  const [updateNonProcessingDay] = useMutation(
    graphql(`
      mutation UpdateNonProcessingDay($input: UpdateNonProcessingDayInput!) {
        updateNonProcessingDay(input: $input) {
          nonProcessingDay {
            ...AdminNonProcessingDayRow
          }
        }
      }
    `),
  );

  const [deleteNonProcessingDay, { loading: deleteLoading }] = useMutation(
    graphql(`
      mutation DeleteNonProcessingDay($input: DeleteNonProcessingDayInput!) {
        deleteNonProcessingDay(input: $input) {
          deletedId
        }
      }
    `),
  );

  const [importNonProcessingDaysFromIcs] = useMutation(
    graphql(`
      mutation ImportNonProcessingDaysFromIcs(
        $input: ImportNonProcessingDaysFromIcsInput!
      ) {
        importNonProcessingDaysFromIcs(input: $input) {
          created
          updated
          nonProcessingDays {
            ...AdminNonProcessingDayRow
          }
        }
      }
    `),
  );

  const rows = (data.nonProcessingDays ?? [])
    .slice()
    .sort((a, b) => b.date.localeCompare(a.date));

  const columns = useMemo<
    ColumnDef<AdminNonProcessingDayRowFragment & { id: string }>[]
  >(
    () => [
      {
        accessorKey: "date",
        header: "Date",
      },
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "tz",
        header: "Timezone",
      },
      {
        accessorKey: "source",
        header: "Source",
        cell: ({ row }) => getSourceLabel(row.original.source),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated at",
        cell: ({ row }) => row.original.updatedAt.toLocaleString(),
      },
      {
        id: "actions",
        header: () => <div className="sr-only">Actions</div>,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="secondary">
                <MoreHorizontal className="size-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  setEditingRow(row.original);
                  setUpsertDialogOpen(true);
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDeletingRow(row.original)}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
          Import from ICS
        </Button>
        <Button variant="outline" onClick={() => setUpsertDialogOpen(true)}>
          Add day
        </Button>
      </div>
      <DataTable
        data={rows}
        columns={columns}
        className="overflow-hidden border"
      />

      <UpsertNonProcessingDayDialog
        key={editingRow?.id ?? "new"}
        row={editingRow}
        open={upsertDialogOpen}
        onOpenChange={(open) => {
          setUpsertDialogOpen(open);
          if (!open && editingRow) {
            setEditingRow(null);
          }
        }}
        onSubmit={async (input) => {
          if (editingRow) {
            await updateNonProcessingDay({
              variables: {
                input: {
                  id: editingRow.id,
                  ...input,
                },
              },
              refetchQueries: "active",
              awaitRefetchQueries: true,
              onCompleted() {
                toast({
                  kind: "success",
                  title: "Non-processing day updated",
                  autoHide: true,
                });
                setUpsertDialogOpen(false);
                setEditingRow(null);
              },
              onError() {
                toast({
                  kind: "error",
                  title: "Failed to update non-processing day",
                  autoHide: true,
                });
              },
            });
            return;
          }

          await createNonProcessingDay({
            variables: {
              input,
            },
            refetchQueries: "active",
            awaitRefetchQueries: true,
            onCompleted() {
              toast({
                kind: "success",
                title: "Non-processing day created",
                autoHide: true,
              });
              setUpsertDialogOpen(false);
            },
            onError() {
              toast({
                kind: "error",
                title: "Failed to create non-processing day",
                autoHide: true,
              });
            },
          });
        }}
      />

      <ImportNonProcessingDaysDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSubmit={async (input) => {
          await importNonProcessingDaysFromIcs({
            variables: { input },
            refetchQueries: "active",
            awaitRefetchQueries: true,
            onCompleted(result) {
              const summary = result.importNonProcessingDaysFromIcs;
              toast({
                kind: "success",
                title: "Imported non-processing days",
                description: `${summary?.created ?? 0} added, ${summary?.updated ?? 0} updated.`,
                autoHide: true,
              });
              setImportDialogOpen(false);
            },
            onError() {
              toast({
                kind: "error",
                title: "Failed to import from ICS link",
                autoHide: true,
              });
            },
          });
        }}
      />

      <Dialog
        open={deletingRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingRow(null);
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Delete non-processing day</DialogTitle>
          </DialogHeader>
          <p>
            {deletingRow
              ? `Delete ${deletingRow.name} (${deletingRow.date})?`
              : null}
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              loading={deleteLoading}
              disabled={deleteLoading || deletingRow === null}
              onClick={async () => {
                if (!deletingRow) {
                  return;
                }

                await deleteNonProcessingDay({
                  variables: {
                    input: {
                      id: deletingRow.id,
                    },
                  },
                  refetchQueries: "active",
                  awaitRefetchQueries: true,
                  onCompleted() {
                    toast({
                      kind: "success",
                      title: "Non-processing day deleted",
                      autoHide: true,
                    });
                    setDeletingRow(null);
                  },
                  onError() {
                    toast({
                      kind: "error",
                      title: "Failed to delete non-processing day",
                      autoHide: true,
                    });
                  },
                });
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UpsertNonProcessingDayDialog({
  row,
  open,
  onOpenChange,
  onSubmit,
}: {
  row: AdminNonProcessingDayRowFragment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: {
    date: string;
    name: string;
    tz: string;
  }) => Promise<void>;
}) {
  const form = useAppForm({
    defaultValues: {
      date: row?.date,
      name: row?.name ?? "",
      tz: row?.tz ?? DEFAULT_TZ,
    } as Partial<DayFormSchema>,
    validators: {
      onSubmit: dayFormSchema,
    },
    async onSubmit(formProps) {
      const formData = dayFormSchema.parse(formProps.value);

      await onSubmit({
        date: formData.date,
        name: formData.name.trim(),
        tz: formData.tz.trim(),
      });
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [form, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {row ? "Edit non-processing day" : "New non-processing day"}
          </DialogTitle>
        </DialogHeader>
        <form.AppForm>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              form.handleSubmit(event);
            }}
          >
            <form.AppField name="date">
              {(field) => <field.InputField label="Date" type="date" />}
            </form.AppField>

            <form.AppField name="name">
              {(field) => (
                <field.InputField
                  label="Name"
                  placeholder="e.g. Christmas Day"
                />
              )}
            </form.AppField>

            <form.AppField name="tz">
              {(field) => (
                <field.InputField
                  label="Timezone"
                  placeholder="e.g. Australia/Sydney"
                />
              )}
            </form.AppField>

            <form.SubmitButton>
              {row ? "Save changes" : "Create non-processing day"}
            </form.SubmitButton>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

function ImportNonProcessingDaysDialog({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { url: string }) => Promise<void>;
}) {
  const form = useAppForm({
    defaultValues: { url: "" } as Partial<ImportSchema>,
    validators: {
      onSubmit: importSchema,
    },
    async onSubmit(formProps) {
      const formData = importSchema.parse(formProps.value);
      await onSubmit({ url: formData.url.trim() });
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [form, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import non-processing days from ICS</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          Paste a link to an ICS calendar feed (e.g. a public holiday calendar).
          Whole-day events are added or updated by date. Existing days are never
          removed.
        </p>
        <form.AppForm>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              form.handleSubmit(event);
            }}
          >
            <form.AppField name="url">
              {(field) => (
                <field.InputField
                  label="ICS link"
                  placeholder="https://example.com/holidays.ics"
                />
              )}
            </form.AppField>

            <form.SubmitButton>Import</form.SubmitButton>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

function getSourceLabel(source: NonProcessingDaySource): string {
  switch (source) {
    case "MANUAL":
      return "Manual";
    case "ICS_IMPORT":
      return "ICS import";
    default:
      return neverFunc(source);
  }
}

// ---------------------------------------------------------------------------
// ICS Calendars tab
// ---------------------------------------------------------------------------

const adminNonProcessingDayIcsCalendarsDocument = graphql(`
  query AdminNonProcessingDayIcsCalendars {
    nonProcessingDayIcsCalendars {
      ...AdminNonProcessingDayIcsCalendarRow
    }
  }

  fragment AdminNonProcessingDayIcsCalendarRow on NonProcessingDayIcsCalendar {
    id
    name
    url
    enabled
    lastSyncedAt
    lastSyncError
    createdAt
    updatedAt
  }
`);

const calendarFormSchema = z.object({
  name: z.string().trim().min(1, { message: "Please enter a name" }),
  url: z.string().trim().url({ message: "Please enter a valid ICS URL" }),
  enabled: z.boolean(),
});

type CalendarFormSchema = z.input<typeof calendarFormSchema>;

function CalendarsTab() {
  const { data } = useSuspenseQuery(adminNonProcessingDayIcsCalendarsDocument);
  const [editingRow, setEditingRow] =
    useState<AdminNonProcessingDayIcsCalendarRowFragment | null>(null);
  const [deletingRow, setDeletingRow] =
    useState<AdminNonProcessingDayIcsCalendarRowFragment | null>(null);
  const [upsertDialogOpen, setUpsertDialogOpen] = useState(false);

  const [createCalendar] = useMutation(
    graphql(`
      mutation CreateNonProcessingDayIcsCalendar(
        $input: CreateNonProcessingDayIcsCalendarInput!
      ) {
        createNonProcessingDayIcsCalendar(input: $input) {
          nonProcessingDayIcsCalendar {
            ...AdminNonProcessingDayIcsCalendarRow
          }
        }
      }
    `),
  );

  const [updateCalendar] = useMutation(
    graphql(`
      mutation UpdateNonProcessingDayIcsCalendar(
        $input: UpdateNonProcessingDayIcsCalendarInput!
      ) {
        updateNonProcessingDayIcsCalendar(input: $input) {
          nonProcessingDayIcsCalendar {
            ...AdminNonProcessingDayIcsCalendarRow
          }
        }
      }
    `),
  );

  const [deleteCalendar, { loading: deleteLoading }] = useMutation(
    graphql(`
      mutation DeleteNonProcessingDayIcsCalendar(
        $input: DeleteNonProcessingDayIcsCalendarInput!
      ) {
        deleteNonProcessingDayIcsCalendar(input: $input) {
          deletedId
        }
      }
    `),
  );

  const rows = (data.nonProcessingDayIcsCalendars ?? [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  const columns = useMemo<
    ColumnDef<AdminNonProcessingDayIcsCalendarRowFragment & { id: string }>[]
  >(
    () => [
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "url",
        header: "ICS URL",
        cell: ({ row }) => (
          <span className="block max-w-md truncate" title={row.original.url}>
            {row.original.url}
          </span>
        ),
      },
      {
        accessorKey: "enabled",
        header: "Enabled",
        cell: ({ row }) => (row.original.enabled ? "Yes" : "No"),
      },
      {
        accessorKey: "lastSyncedAt",
        header: "Last synced",
        cell: ({ row }) =>
          row.original.lastSyncedAt
            ? row.original.lastSyncedAt.toLocaleString()
            : "Never",
      },
      {
        accessorKey: "lastSyncError",
        header: "Last error",
        cell: ({ row }) =>
          row.original.lastSyncError ? (
            <span
              className="block max-w-xs truncate text-destructive"
              title={row.original.lastSyncError}
            >
              {row.original.lastSyncError}
            </span>
          ) : (
            "-"
          ),
      },
      {
        id: "actions",
        header: () => <div className="sr-only">Actions</div>,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="secondary">
                <MoreHorizontal className="size-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  setEditingRow(row.original);
                  setUpsertDialogOpen(true);
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDeletingRow(row.original)}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => setUpsertDialogOpen(true)}>
          Add calendar
        </Button>
      </div>
      <DataTable
        data={rows}
        columns={columns}
        className="overflow-hidden border"
      />

      <UpsertCalendarDialog
        key={editingRow?.id ?? "new"}
        row={editingRow}
        open={upsertDialogOpen}
        onOpenChange={(open) => {
          setUpsertDialogOpen(open);
          if (!open && editingRow) {
            setEditingRow(null);
          }
        }}
        onSubmit={async (input) => {
          if (editingRow) {
            await updateCalendar({
              variables: { input: { id: editingRow.id, ...input } },
              refetchQueries: "active",
              awaitRefetchQueries: true,
              onCompleted() {
                toast({
                  kind: "success",
                  title: "Calendar updated",
                  autoHide: true,
                });
                setUpsertDialogOpen(false);
                setEditingRow(null);
              },
              onError() {
                toast({
                  kind: "error",
                  title: "Failed to update calendar",
                  autoHide: true,
                });
              },
            });
            return;
          }

          await createCalendar({
            variables: { input },
            refetchQueries: "active",
            awaitRefetchQueries: true,
            onCompleted() {
              toast({
                kind: "success",
                title: "Calendar added",
                autoHide: true,
              });
              setUpsertDialogOpen(false);
            },
            onError() {
              toast({
                kind: "error",
                title: "Failed to add calendar",
                autoHide: true,
              });
            },
          });
        }}
      />

      <Dialog
        open={deletingRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingRow(null);
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Delete calendar</DialogTitle>
          </DialogHeader>
          <p>
            {deletingRow
              ? `Delete "${deletingRow.name}"? Days already imported from it are kept.`
              : null}
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              loading={deleteLoading}
              disabled={deleteLoading || deletingRow === null}
              onClick={async () => {
                if (!deletingRow) {
                  return;
                }

                await deleteCalendar({
                  variables: { input: { id: deletingRow.id } },
                  refetchQueries: "active",
                  awaitRefetchQueries: true,
                  onCompleted() {
                    toast({
                      kind: "success",
                      title: "Calendar deleted",
                      autoHide: true,
                    });
                    setDeletingRow(null);
                  },
                  onError() {
                    toast({
                      kind: "error",
                      title: "Failed to delete calendar",
                      autoHide: true,
                    });
                  },
                });
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UpsertCalendarDialog({
  row,
  open,
  onOpenChange,
  onSubmit,
}: {
  row: AdminNonProcessingDayIcsCalendarRowFragment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: {
    name: string;
    url: string;
    enabled: boolean;
  }) => Promise<void>;
}) {
  const form = useAppForm({
    defaultValues: {
      name: row?.name ?? "",
      url: row?.url ?? "",
      enabled: row?.enabled ?? true,
    } as Partial<CalendarFormSchema>,
    validators: {
      onSubmit: calendarFormSchema,
    },
    async onSubmit(formProps) {
      const formData = calendarFormSchema.parse(formProps.value);

      await onSubmit({
        name: formData.name.trim(),
        url: formData.url.trim(),
        enabled: formData.enabled,
      });
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [form, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{row ? "Edit calendar" : "New calendar"}</DialogTitle>
        </DialogHeader>
        <form.AppForm>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              form.handleSubmit(event);
            }}
          >
            <form.AppField name="name">
              {(field) => (
                <field.InputField
                  label="Name"
                  placeholder="e.g. Australian public holidays"
                />
              )}
            </form.AppField>

            <form.AppField name="url">
              {(field) => (
                <field.InputField
                  label="ICS link"
                  placeholder="https://example.com/holidays.ics"
                />
              )}
            </form.AppField>

            <form.AppField name="enabled">
              {(field) => (
                <field.SwitchField
                  label="Enabled"
                  description="Include this calendar in the daily sync."
                />
              )}
            </form.AppField>

            <form.SubmitButton>
              {row ? "Save changes" : "Add calendar"}
            </form.SubmitButton>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}
